// Blitz ▾ „Workflow auslösen“ und „Workflows verwalten“ (Agent 4C, Runde 4). Eingebaut im Kopf jeder Smart View und
// Lead-Liste (`smartview/ListenKopfRechts.tsx`) und im Menü „…“ am Lead (`lead/LeadKopf.tsx`); beide rufen nur
// `<WorkflowBlitz kontext=… />` bzw. `useWorkflowMenue(kontext)`. Gesendet wird von hier aus nie etwas: Mails und SMS
// entstehen als Entwurf zur Prüfung, alles andere sind Aufgaben und Änderungen am Lead mit dem Schlüssel einer Person.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { Ziel } from "../api/leadsNeu";
import { workflowsApi, type AusloesenErgebnis, type Workflow, type WorkflowLauf, type ZielLaeufe } from "../api/workflows";
import { useAuth } from "../auth/AuthContext";
import { Laden } from "../components/Bausteine";
import { Dialog } from "../components/Dialog";
import { FehlerBox } from "../components/Fehler";
import { Icon } from "../components/Icon";
import { MenueKnopf, type MenuePunkt } from "../components/Menue";
import { useMeldung } from "../components/Meldungen";
import { Auswahl } from "../components/Auswahl";
import { ausloeserText } from "./WorkflowListe";
import { useMeta } from "../daten/meta";
import { LaeufeTabelle, type LaufAktion } from "./LaeufeTabelle";
import { MASSEN_AB, MassenBestaetigung, zahlAusEingabe } from "./MassenBestaetigung";
import "./workflows.css";

/** Wo der Blitz steht und auf wen sich die Punkte beziehen. */
export type WorkflowKontext =
  | {
      art: "smartview";
      /** gespeicherte Smart View; fehlt bei der Leads-Liste */
      viewId?: string;
      titel: string;
      /** alle Treffer der Liste */
      ziel: Ziel;
      /** markierte Leads; leer = alle Treffer */
      markierte: string[];
      trefferZahl: number | null;
    }
  | { art: "lead"; leadId: string; leadName: string }
  /** vom Kontakt aus (Menü „…“ der Kontaktkarte): der Lauf merkt sich den Kontakt */
  | { art: "kontakt"; leadId: string; leadName: string; contactId: string; contactName: string };

export interface WorkflowMenue {
  /** Menüpunkte für ein beliebiges Menü (Blitz-Knopf, Lead-„…“) */
  punkte: MenuePunkt[];
  /** Dialoge dazu; einmal irgendwo im selben Bauteil ausgeben */
  dialoge: ReactNode;
}

function zielVon(k: WorkflowKontext): Ziel {
  if (k.art === "smartview") return k.markierte.length ? { lead_ids: k.markierte } : k.ziel;
  return { lead_ids: [k.leadId] };
}

function zielText(k: WorkflowKontext): string {
  if (k.art === "smartview") {
    if (k.markierte.length) return `${k.markierte.length.toLocaleString("de-DE")} markierte${k.markierte.length === 1 ? "r Lead" : " Leads"}`;
    return k.trefferZahl !== null ? `alle ${k.trefferZahl.toLocaleString("de-DE")} Treffer von „${k.titel}“` : `alle Treffer von „${k.titel}“`;
  }
  if (k.art === "kontakt") return `${k.contactName || "Kontakt"} bei ${k.leadName}`;
  return k.leadName;
}

function gruppiereGruende(gruende: { grund: string }[]): [string, number][] {
  const z = new Map<string, number>();
  for (const g of gruende) z.set(g.grund, (z.get(g.grund) ?? 0) + 1);
  return [...z.entries()].sort((a, b) => b[1] - a[1]);
}

// --- Workflow auslösen -------------------------------------------------------------------------------------------

function AusloesenDialog({ kontext, onSchliessen }: { kontext: WorkflowKontext; onSchliessen: () => void }) {
  const meta = useMeta();
  const meldung = useMeldung();
  const [workflows, setWorkflows] = useState<Workflow[] | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [wahl, setWahl] = useState<string | null>(null);
  const [vorschau, setVorschau] = useState<AusloesenErgebnis | null>(null);
  const [pruefe, setPruefe] = useState(false);
  const [startet, setStartet] = useState(false);
  const [ergebnis, setErgebnis] = useState<AusloesenErgebnis | null>(null);
  const [zahlEingabe, setZahlEingabe] = useState("");
  const ziel = useMemo(() => zielVon(kontext), [kontext]);
  const contactId = kontext.art === "kontakt" ? kontext.contactId : undefined;

  useEffect(() => {
    let weg = false;
    workflowsApi
      .liste({ status: "aktiv", limit: 200 })
      .then((r) => {
        if (weg) return;
        setWorkflows(r.data);
        if (r.data.length === 1) setWahl(r.data[0].id);
      })
      .catch((e) => !weg && setFehler(e));
    return () => {
      weg = true;
    };
  }, []);

  useEffect(() => {
    setVorschau(null);
    setZahlEingabe("");
    if (!wahl) return;
    let weg = false;
    setPruefe(true);
    workflowsApi
      .ausloesen(wahl, ziel, { vorschau: true, contact_id: contactId })
      .then((r) => !weg && setVorschau(r))
      .catch((e) => !weg && setFehler(e))
      .finally(() => !weg && setPruefe(false));
    return () => {
      weg = true;
    };
  }, [wahl, ziel, contactId]);

  const starten = async () => {
    if (!wahl) return;
    setStartet(true);
    try {
      const r = await workflowsApi.ausloesen(wahl, ziel, { bestaetigt: true, contact_id: contactId });
      setErgebnis(r);
      meldung.ok(r.eingeschrieben ? `${r.eingeschrieben.toLocaleString("de-DE")} ${r.eingeschrieben === 1 ? "Lead" : "Leads"} eingeschrieben.` : "Niemand eingeschrieben.");
    } catch (e) {
      meldung.fehler(e, "Workflow nicht gestartet");
    } finally {
      setStartet(false);
    }
  };

  const gewaehlt = workflows?.find((w) => w.id === wahl) ?? null;
  // Ab MASSEN_AB Leads genügt ein Klick nicht: die Zahl muss eingetippt werden
  const massenstart = !!vorschau && !ergebnis && vorschau.eingeschrieben >= MASSEN_AB;
  const massenFrei = !massenstart || zahlAusEingabe(zahlEingabe) === vorschau?.eingeschrieben;
  const zeige = ergebnis ?? vorschau;
  const gruende = zeige ? gruppiereGruende(zeige.gruende) : [];

  return (
    <Dialog
      titel="Workflow auslösen"
      offen
      onSchliessen={onSchliessen}
      breite={520}
      fuss={
        ergebnis ? (
          <>
            <Link to="/workflows/laeufe" className="knopf" onClick={onSchliessen}>
              Läufe ansehen
            </Link>
            <button type="button" className="knopf knopf--primaer" onClick={onSchliessen}>
              Fertig
            </button>
          </>
        ) : (
          <>
            <button type="button" className="knopf" onClick={onSchliessen}>
              Abbrechen
            </button>
            <button type="button" className="knopf knopf--primaer" disabled={!wahl || startet || pruefe || !vorschau || vorschau.eingeschrieben === 0 || !massenFrei} onClick={() => void starten()}>
              {startet ? "Startet …" : vorschau && vorschau.eingeschrieben ? `Für ${vorschau.eingeschrieben.toLocaleString("de-DE")} ${vorschau.eingeschrieben === 1 ? "Lead" : "Leads"} starten` : "Starten"}
            </button>
          </>
        )
      }
    >
      <div className="wf-dialog">
        <p className="wf-dialog__ziel">
          <Icon name="leads" groesse={14} /> <strong>{zielText(kontext)}</strong>
        </p>
        {fehler ? <FehlerBox fehler={fehler} kompakt /> : null}
        {!workflows && !fehler ? <Laden klein /> : null}
        {workflows && workflows.length === 0 ? (
          <div className="hinweis">
            <Icon name="info" groesse={15} />
            <span>
              Es gibt noch keinen aktiven Workflow. <Link to="/workflows" onClick={onSchliessen}>Workflows öffnen</Link>
            </span>
          </div>
        ) : null}
        {workflows && workflows.length > 0 && !ergebnis ? (
          <div className="feld wf-feld">
            <span className="feld__label">Workflow</span>
            <Auswahl
              optionen={workflows.map((w) => ({ wert: w.id, label: w.name, unter: ausloeserText(w, (id) => meta.status(id)?.label, (id) => meta.typ(id)?.name) }))}
              wert={wahl}
              platzhalter="Workflow wählen …"
              onWahl={(w) => setWahl((w as string | null) ?? null)}
            />
            {gewaehlt?.beschreibung ? <span className="leise wf-feld__hinweis">{gewaehlt.beschreibung}</span> : null}
          </div>
        ) : null}
        {pruefe ? <Laden klein text="Prüft, wer eingeschrieben werden kann …" /> : null}
        {zeige && !pruefe ? (
          <div className={`wf-vorschau${ergebnis ? " wf-vorschau--fertig" : ""}`} role="status">
            <div>
              {ergebnis ? (
                <>
                  <strong>{zeige.eingeschrieben.toLocaleString("de-DE")}</strong> {zeige.eingeschrieben === 1 ? "Lead ist" : "Leads sind"} eingeschrieben.
                </>
              ) : (
                <>
                  Startet für <strong>{zeige.eingeschrieben.toLocaleString("de-DE")}</strong> von {(zeige.leads ?? zeige.eingeschrieben + zeige.uebersprungen).toLocaleString("de-DE")} {zeige.leads === 1 ? "Lead" : "Leads"}.
                </>
              )}
              {zeige.uebersprungen ? <span className="leise"> {zeige.uebersprungen.toLocaleString("de-DE")} übersprungen.</span> : null}
            </div>
            {gruende.length ? (
              <ul className="wf-vorschau__gruende">
                {gruende.slice(0, 6).map(([grund, anzahl]) => (
                  <li key={grund}>
                    <span className="leise">{anzahl}× </span>
                    {grund}
                  </li>
                ))}
              </ul>
            ) : null}
            {zeige.gekappt ? <div className="text-warnung">Das Ziel hat mehr als 2.000 Treffer, es werden nur die ersten 2.000 verwendet.</div> : null}
            {ergebnis ? <div className="leise">Die Schritte laufen in den nächsten Sekunden. Mails und SMS entstehen nur als Entwurf zur Prüfung.</div> : null}
          </div>
        ) : null}
        {massenstart && gewaehlt && vorschau && !pruefe ? (
          <MassenBestaetigung workflow={gewaehlt} anzahl={vorschau.eingeschrieben} eingabe={zahlEingabe} onEingabe={setZahlEingabe} />
        ) : null}
        {!ergebnis ? (
          <p className="leise wf-dialog__hinweis">Der Workflow arbeitet mit dem Schlüssel der Person, die in den Einstellungen des Workflows steht (sonst deinem). Nichts wird automatisch gesendet.</p>
        ) : null}
      </div>
    </Dialog>
  );
}

// --- Workflows verwalten -----------------------------------------------------------------------------------------

function VerwaltenDialog({ kontext, onSchliessen }: { kontext: WorkflowKontext; onSchliessen: () => void }) {
  const meldung = useMeldung();
  const { nutzer } = useAuth();
  const kannVerwalten = !!nutzer?.permissions.includes("workflows_verwalten");
  const ziel = useMemo(() => zielVon(kontext), [kontext]);
  const [laeufe, setLaeufe] = useState<WorkflowLauf[] | null>(null);
  const [zielLaeufe, setZielLaeufe] = useState<ZielLaeufe | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [beenden, setBeenden] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      if (kontext.art === "smartview") setZielLaeufe(await workflowsApi.zielLaeufe(ziel));
      else {
        const r = await workflowsApi.amLead(kontext.leadId);
        setLaeufe(kontext.art === "kontakt" ? r.laeufe.filter((l) => !l.contact_id || l.contact_id === kontext.contactId) : r.laeufe);
      }
      setFehler(null);
    } catch (e) {
      setFehler(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kontext, ziel]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const einzeln = async (l: { id: string }, a: LaufAktion) => {
    try {
      await workflowsApi.laufAktion(l.id, a);
      await laden();
    } catch (e) {
      meldung.fehler(e, "Lauf nicht geändert");
    }
  };

  const gemeinsam = async (aktion: LaufAktion, workflowId?: string) => {
    try {
      const r = await workflowsApi.zielAktion(ziel, aktion, workflowId);
      meldung.ok(r.geaendert ? `${r.geaendert.toLocaleString("de-DE")} ${r.geaendert === 1 ? "Lauf" : "Läufe"} ${aktion === "pausieren" ? "pausiert" : aktion === "fortsetzen" ? "fortgesetzt" : "beendet"}.` : "Nichts zu ändern.");
      setBeenden(null);
      await laden();
    } catch (e) {
      meldung.fehler(e, "Läufe nicht geändert");
    }
  };

  const alleLink = kontext.art === "smartview" ? "/workflows/laeufe" : `/workflows/laeufe?lead_id=${encodeURIComponent(kontext.leadId)}`;
  const gesamt = zielLaeufe ? zielLaeufe.workflows.reduce((s, w) => s + w.aktiv + w.pausiert + w.fehler, 0) : 0;

  return (
    <Dialog
      titel="Workflows verwalten"
      offen
      onSchliessen={onSchliessen}
      breite={kontext.art === "smartview" ? 640 : 780}
      fuss={
        <>
          <Link to={alleLink} className="knopf" onClick={onSchliessen}>
            Alle Läufe ansehen
          </Link>
          <button type="button" className="knopf knopf--primaer" onClick={onSchliessen}>
            Fertig
          </button>
        </>
      }
    >
      <div className="wf-dialog">
        <p className="wf-dialog__ziel">
          <Icon name="leads" groesse={14} /> <strong>{zielText(kontext)}</strong>
        </p>
        {fehler ? <FehlerBox fehler={fehler} onNochmal={() => void laden()} kompakt /> : null}
        {!fehler && !laeufe && !zielLaeufe ? <Laden klein /> : null}

        {kontext.art !== "smartview" && laeufe ? (
          laeufe.length ? (
            <LaeufeTabelle laeufe={laeufe} zeigeLead={false} kannVerwalten={kannVerwalten} onAktion={einzeln} />
          ) : (
            <p className="leise wf-leer">Für diesen Lead läuft und lief noch kein Workflow.</p>
          )
        ) : null}

        {kontext.art === "smartview" && zielLaeufe ? (
          zielLaeufe.workflows.length ? (
            <>
              <p className="leise">
                {zielLaeufe.leads_mit_laeufen.toLocaleString("de-DE")} von {zielLaeufe.leads.toLocaleString("de-DE")} Leads haben offene oder fehlerhafte Läufe.
              </p>
              <table className="tabelle tabelle--einfach wf-verwalten">
                <thead>
                  <tr>
                    <th>Workflow</th>
                    <th className="wf-zahl">Aktiv</th>
                    <th className="wf-zahl">Pausiert</th>
                    <th className="wf-zahl">Fehler</th>
                    <th aria-label="Aktionen" />
                  </tr>
                </thead>
                <tbody>
                  {zielLaeufe.workflows.map((w) => (
                    <tr key={w.workflow_id}>
                      <td>
                        <Link to={`/workflows/${encodeURIComponent(w.workflow_id)}`} onClick={onSchliessen}>
                          {w.name ?? w.workflow_id}
                        </Link>
                      </td>
                      <td className="wf-zahl">{w.aktiv}</td>
                      <td className="wf-zahl">{w.pausiert}</td>
                      <td className={`wf-zahl${w.fehler ? " text-gefahr" : ""}`}>{w.fehler}</td>
                      <td className="wf-zelle-aktion">
                        {kannVerwalten ? (
                          <>
                            <button type="button" className="knopf knopf--klein knopf--leise" disabled={!w.aktiv} onClick={() => void gemeinsam("pausieren", w.workflow_id)}>
                              Pausieren
                            </button>
                            <button type="button" className="knopf knopf--klein knopf--leise" disabled={!w.pausiert && !w.fehler} onClick={() => void gemeinsam("fortsetzen", w.workflow_id)}>
                              Fortsetzen
                            </button>
                            {beenden === w.workflow_id ? (
                              <button type="button" className="knopf knopf--klein knopf--gefahr" onClick={() => void gemeinsam("abbrechen", w.workflow_id)} onBlur={() => setBeenden(null)} autoFocus>
                                Wirklich beenden?
                              </button>
                            ) : (
                              <button type="button" className="knopf knopf--klein knopf--leise" onClick={() => setBeenden(w.workflow_id)}>
                                Beenden
                              </button>
                            )}
                          </>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {kannVerwalten && zielLaeufe.workflows.length > 1 ? (
                <div className="wf-verwalten__alle">
                  <span className="leise">Alle {gesamt.toLocaleString("de-DE")} Läufe:</span>
                  <button type="button" className="knopf knopf--klein" onClick={() => void gemeinsam("pausieren")}>
                    Alle pausieren
                  </button>
                  <button type="button" className="knopf knopf--klein" onClick={() => void gemeinsam("fortsetzen")}>
                    Alle fortsetzen
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <p className="leise wf-leer">Für diese Leads läuft kein Workflow.</p>
          )
        ) : null}
        {!kannVerwalten ? <p className="leise wf-dialog__hinweis">Pausieren, Fortsetzen und Beenden dürfen Admins und Super User.</p> : null}
      </div>
    </Dialog>
  );
}

// --- Einbauplätze ------------------------------------------------------------------------------------------------

export function useWorkflowMenue(kontext: WorkflowKontext): WorkflowMenue {
  // Der Dialog arbeitet mit dem Stand beim Öffnen (Markierung und Treffer ändern sich sonst unter ihm).
  const [offen, setOffen] = useState<{ was: "ausloesen" | "verwalten"; kontext: WorkflowKontext } | null>(null);
  const { nutzer } = useAuth();
  const kannVerwalten = !!nutzer?.permissions.includes("workflows_verwalten");
  const punkte: MenuePunkt[] = [
    {
      label: "Workflow auslösen",
      icon: "blitz",
      hinweis: kannVerwalten ? "Einen Workflow für diese Leads starten" : "Dafür brauchst du die Rolle Admin oder Super User",
      deaktiviert: !kannVerwalten,
      onClick: () => setOffen({ was: "ausloesen", kontext }),
    },
    {
      label: "Workflows verwalten",
      icon: "einstellungen",
      hinweis: "Laufende Workflows dieser Leads ansehen, pausieren, fortsetzen oder beenden",
      onClick: () => setOffen({ was: "verwalten", kontext }),
    },
  ];
  const dialoge =
    offen?.was === "ausloesen" ? (
      <AusloesenDialog kontext={offen.kontext} onSchliessen={() => setOffen(null)} />
    ) : offen?.was === "verwalten" ? (
      <VerwaltenDialog kontext={offen.kontext} onSchliessen={() => setOffen(null)} />
    ) : null;
  return { punkte, dialoge };
}

/** Der Blitz-Knopf mit Menü, wie im Kopf der Smart Views. */
export function WorkflowBlitz({ kontext }: { kontext: WorkflowKontext }) {
  const { punkte, dialoge } = useWorkflowMenue(kontext);
  return (
    <>
      <MenueKnopf knopfKlasse="symbolknopf symbolknopf--rand wf-blitz-knopf" titel="Workflow auslösen" punkte={punkte} ausrichtung="end" breite={280}>
        <Icon name="blitz" groesse={15} />
        <Icon name="chevron-unten" groesse={11} />
      </MenueKnopf>
      {dialoge}
    </>
  );
}
